import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import {
  ChartLineUp, CurrencyDollar, Lightning, ChatCircleDots,
  CalendarCheck, Trophy, ArrowDown,
} from "@phosphor-icons/react";

const fmtMoney = (cents) =>
  ((cents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", maximumFractionDigits: 0,
  });

const fmtNum = (n) => (n || 0).toLocaleString("pt-BR");

// ─── KPI card — clean premium layout, never overflows ───────────────
function KPI({ icon: Icon, label, value, sub }) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-5 min-w-0"
      data-testid={`roi-kpi-${label}`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium leading-snug">
          {label}
        </div>
        <div className="w-7 h-7 rounded-full bg-slate-50 text-slate-500 flex items-center justify-center shrink-0">
          <Icon size={13} weight="regular" />
        </div>
      </div>
      <div className="font-heading text-[28px] leading-none font-semibold text-slate-900 tracking-tight truncate">
        {value}
      </div>
      {sub && <div className="text-[11px] text-slate-400 mt-2 truncate">{sub}</div>}
    </div>
  );
}

// ─── Funnel stage — premium step with conversion % between stages ───
function FunnelStage({ label, value, prev, accent = "bg-slate-900", first }) {
  // Cap at 100% — funnel stages can legitimately diverge (an appointment
  // may be created out-of-band of a reply), but a conversion > 100%
  // confuses the operator. We display "100%+" in those rare cases so
  // the operator still notices it's worth investigating.
  const rawPct = prev ? (value / prev) * 100 : null;
  const pctText = rawPct == null ? "—"
    : rawPct > 100 ? "100%+"
    : `${Math.round(rawPct)}%`;
  return (
    <div className="space-y-2">
      {!first && (
        <div className="flex items-center justify-center text-[11px] text-slate-400" data-testid={`funnel-conv-${label}`}>
          <ArrowDown size={11} weight="bold" />
          <span className="ml-1 tabular-nums">{pctText} segue pra próxima etapa</span>
        </div>
      )}
      <div
        className="flex items-center justify-between gap-4 bg-white border border-slate-200 rounded-md px-4 py-3"
        data-testid={`funnel-stage-${label}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className={`block w-1.5 h-8 rounded-full ${accent}`} />
          <div className="min-w-0">
            <div className="text-[13px] font-medium text-slate-900 tracking-tight truncate">{label}</div>
          </div>
        </div>
        <div className="font-heading text-xl font-semibold text-slate-900 tabular-nums shrink-0">
          {fmtNum(value)}
        </div>
      </div>
    </div>
  );
}

export default function ROI() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);

  const load = async () => {
    const { data } = await api.get("/roi", { params: { days } });
    setData(data);
  };

  useEffect(() => { load(); }, [days]); // eslint-disable-line

  if (!data) return <Topbar title="Impacto do AgentUS" subtitle="Carregando…" />;

  const t = data.totals;

  return (
    <>
      <Topbar
        title="Impacto do AgentUS"
        subtitle="O que a IA rendeu no período — receita, agendamentos e comparecimentos gerados por cada automação. Isto não é o faturamento do seu ERP; é só o que o AgentUS produziu."
        actions={
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="roi-period-select"
            className="rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-[12px] font-medium text-slate-700 hover:border-slate-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 cursor-pointer"
          >
            <option value={30}>Últimos 30 dias</option>
            <option value={90}>Últimos 90 dias</option>
            <option value={180}>Últimos 180 dias</option>
            <option value={365}>Último ano</option>
          </select>
        }
      />
      <div className="p-6 md:p-8 space-y-8">
        {/* KPIs — 1/2/3 columns responsively, never crammed */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="roi-kpis">
          <KPI
            icon={CurrencyDollar} label="Receita estimada"
            value={fmtMoney(t.revenue_cents)}
            sub={`${fmtNum(t.attended)} comparecimentos`}
          />
          <KPI
            icon={CalendarCheck} label="Agendamentos"
            value={fmtNum(t.appointments_created)}
            sub={(() => {
              if (t.replies_received <= 0) return "Aguardando dados de respostas";
              const pct = (t.appointments_created / t.replies_received) * 100;
              if (pct > 100) return "Mais agendamentos que respostas — fluxo paralelo (telefone/balcão)";
              return `${Math.round(pct)}% das respostas viraram agendamento`;
            })()}
          />
          <KPI
            icon={Trophy} label="Comparecimentos"
            value={fmtNum(t.attended)}
            sub={t.appointments_created > 0 ? `${Math.min(100, Math.round((t.attended / t.appointments_created) * 100))}% dos agendamentos compareceram` : "—"}
          />
          <KPI
            icon={Lightning} label="Eventos criados"
            value={fmtNum(t.events_created)}
            sub="Gatilhos disparados pelas suas automações"
          />
          <KPI
            icon={ChatCircleDots} label="Mensagens enviadas"
            value={fmtNum(t.messages_sent)}
          />
          <KPI
            icon={ChatCircleDots} label="Respostas recebidas"
            value={fmtNum(t.replies_received)}
            sub={t.messages_sent > 0 ? `Taxa de resposta ${Math.min(100, Math.round((t.replies_received / t.messages_sent) * 100))}%` : "—"}
          />
        </div>

        {/* Funnel — premium step-by-step view */}
        <section data-testid="roi-funnel">
          <header className="flex items-center gap-2 mb-4">
            <ChartLineUp size={16} className="text-slate-500" weight="regular" />
            <h3 className="font-heading font-semibold tracking-tight text-slate-900 text-[15px]">
              Como uma mensagem vira receita
            </h3>
          </header>
          <div className="space-y-1.5 max-w-2xl">
            <FunnelStage label="Eventos criados"    value={t.events_created}        first accent="bg-slate-400" />
            <FunnelStage label="Mensagens enviadas" value={t.messages_sent}        prev={t.events_created}        accent="bg-slate-500" />
            <FunnelStage label="Respostas"          value={t.replies_received}     prev={t.messages_sent}         accent="bg-slate-600" />
            <FunnelStage label="Agendamentos"       value={t.appointments_created} prev={t.replies_received}      accent="bg-slate-700" />
            <FunnelStage label="Comparecimentos"    value={t.attended}             prev={t.appointments_created}  accent="bg-emerald-600" />
          </div>
        </section>

        {/* Per-rule table */}
        <section className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="roi-rules-table">
          <header className="px-5 py-4 border-b border-slate-200">
            <h3 className="font-heading font-semibold tracking-tight text-slate-900 text-[15px]">Detalhamento por automação</h3>
            <p className="text-xs text-slate-500 mt-0.5">Conversão é a porcentagem de eventos que viraram comparecimento.</p>
          </header>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: "880px" }}>
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-4 py-3 text-[11px] font-medium text-slate-600 uppercase tracking-wider">Automação</th>
                  <th className="px-4 py-3 text-[11px] font-medium text-slate-600 uppercase tracking-wider text-right">Eventos</th>
                  <th className="px-4 py-3 text-[11px] font-medium text-slate-600 uppercase tracking-wider text-right">Msgs</th>
                  <th className="px-4 py-3 text-[11px] font-medium text-slate-600 uppercase tracking-wider text-right">Respostas</th>
                  <th className="px-4 py-3 text-[11px] font-medium text-slate-600 uppercase tracking-wider text-right">Agend.</th>
                  <th className="px-4 py-3 text-[11px] font-medium text-slate-600 uppercase tracking-wider text-right">Compareceu</th>
                  <th className="px-4 py-3 text-[11px] font-medium text-slate-600 uppercase tracking-wider text-right">Conversão</th>
                  <th className="px-4 py-3 text-[11px] font-medium text-slate-600 uppercase tracking-wider text-right">Receita</th>
                </tr>
              </thead>
              <tbody>
                {data.rules.length === 0 && (
                  <tr><td colSpan="8" className="p-8 text-center text-slate-500 text-sm">
                    Sem automações cadastradas — vá em "Automações inteligentes" pra ativar suas primeiras.
                  </td></tr>
                )}
                {data.rules.map((r) => (
                  <tr key={r.rule_id} className="border-t border-slate-100 hover:bg-slate-50/70" data-testid={`roi-row-${r.rule_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 tracking-tight">{r.rule_name}</div>
                      <div className="font-mono text-[11px] text-slate-400 mt-0.5">{r.trigger}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-slate-700">{fmtNum(r.events_created)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-slate-700">{fmtNum(r.messages_sent)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-slate-700">{fmtNum(r.replies_received)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-slate-700">{fmtNum(r.appointments_created)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-slate-700">{fmtNum(r.attended)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums font-semibold text-slate-900">{(r.conversion_rate * 100).toFixed(1)}%</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums font-semibold text-emerald-700">{fmtMoney(r.revenue_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  );
}
